// ============================================================================
// MarketWizardPage — пошаговая заявка клиента (4 шага):
//   1) категория услуги; 2) район и адрес; 3) описание и фото; 4) телефон.
// Пишет ТОЛЬКО существующие реквизиты mkt_requests. Фото прикрепляются к
// созданной заявке штатным механизмом «Файлов» (ownerId = id заявки).
// Офлайн: заявка уходит в durable-очередь SyncEngine; фото к ещё не созданной
// на сервере заявке не прикрепляем (ссылка была бы на временный id).
//
// ЧЕРНОВИК: текстовые поля и шаг автоматически сохраняются на устройстве и
// восстанавливаются ПОСЛЕ ВХОДА (resumeDraft) — гость не теряет ввод, когда
// его отправляют на вход/регистрацию. После успешной отправки черновик
// удаляется. Фото — не сериализуются (File), их нужно приложить заново.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { logErrorToJournal } from '@/lib/errors';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import { settingsStore } from '@/lib/settingsStore';
import {
  loadMarketCategories,
  loadMarketRequest,
  loadMyMarketPhoneGate,
  marketPhoneState,
  toMarketFailure,
  type MarketCategory,
  type MarketFailure,
  type MarketPhoneGate,
  type MarketPhoneState,
  type PhotoUploadResult,
} from '@/lib/market';
import { createMarketRequest, uploadMarketRequestPhotos } from '@/lib/marketActions';
import {
  EMPTY_MARKET_GEO,
  marketGeoHasValue,
  validateMarketGeo,
  type MarketGeoValue,
} from '@/lib/marketGeo';
import {
  composeMarketAddress,
  loadMarketAddresses,
  loadMarketClientProfile,
  rememberMarketAddressFromText,
  type MarketSavedAddress,
} from '@/lib/marketProfile';
import {
  MARKET_MAX_PHOTO_BYTES,
  MARKET_MAX_PHOTOS,
  MARKET_WIZARD_STEPS,
} from '@/lib/marketConstants';
import { MarketError } from '@/components/market/MarketState';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';
import { AuthRequiredScreen } from '@/components/auth/AuthRequiredScreen';
import {
  clearMarketDraft,
  clearMarketResume,
  loadMarketDraft,
  saveMarketDraft,
  type MarketDraftStep,
} from '@/components/market/marketDraft';
import {
  MarketAddressStep,
  MarketCategoryStep,
  MarketDescriptionStep,
  MarketPhoneStep,
  StepDots,
} from '@/components/market/MarketWizardSteps';
import {
  MarketWizardSuccess,
  type WizardSuccessState,
} from '@/components/market/MarketWizardSuccess';

interface MarketWizardPageProps {
  initialCategoryId?: string;
  /** Восстановить черновик пользователя (возврат после входа / «Продолжить»). */
  resumeDraft?: boolean;
  /** Гость: мастер не открывается — только вход/регистрация. */
  onRequireAuth: () => void;
  onCancel: () => void;
  onOpenRequest: (requestId: string) => void;
  onOpenRequests: () => void;
  onHome: () => void;
}

export function MarketWizardPage({
  initialCategoryId,
  resumeDraft,
  onRequireAuth,
  onCancel,
  onOpenRequest,
  onOpenRequests,
  onHome,
}: MarketWizardPageProps) {
  const { t } = useI18n();
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  // Черновик читается ОДИН раз при открытии мастера (не на каждый рендер).
  const [restoredDraft] = useState(() => (resumeDraft ? loadMarketDraft(userId) : null));
  const [step, setStep] = useState<number>(restoredDraft?.step || 1);
  const [categories, setCategories] = useState<MarketCategory[] | null>(null);
  const [categoryFailure, setCategoryFailure] = useState<MarketFailure | null>(null);
  const [categoryTick, setCategoryTick] = useState(0);
  const [categoryId, setCategoryId] = useState(initialCategoryId || restoredDraft?.categoryId || '');
  const [address, setAddress] = useState(restoredDraft?.address || '');
  const [descr, setDescr] = useState(restoredDraft?.descr || '');
  const [phone, setPhone] = useState(restoredDraft?.phone || '');
  // Гео-схема заявки: страна/регион/город/район, координаты, радиус поиска.
  const [geo, setGeo] = useState<MarketGeoValue>(restoredDraft?.geo || { ...EMPTY_MARKET_GEO });
  // «Мои адреса» (user_settings профиля) — быстрый выбор на шаге адреса.
  // Настройки гидратируются с сервера; до гидрации список пуст (не мок).
  const [savedAddresses, setSavedAddresses] = useState<MarketSavedAddress[]>([]);
  const [saveToAddressBook, setSaveToAddressBook] = useState(true);
  const phonePrefilledRef = useRef(Boolean(restoredDraft?.phone));
  /** Телефон профиля и серверный гейт: номер заявки сверяется с профилем. */
  const [profilePhone, setProfilePhone] = useState('');
  const [phoneGate, setPhoneGate] = useState<MarketPhoneGate | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoError, setPhotoError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [failure, setFailure] = useState<MarketFailure | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<WizardSuccessState | null>(null);
  const [savedAt, setSavedAt] = useState<number>(restoredDraft?.updatedAt || 0);

  const draftRef = useRef({ step, categoryId, address, descr, phone, geo });
  draftRef.current = { step, categoryId, address, descr, phone, geo };

  const persistDraft = useCallback((nextStep: number = step) => {
    if (!userId) return;
    const current = draftRef.current;
    const hasContent = Boolean(
      current.categoryId.trim()
      || current.address.trim()
      || current.descr.trim()
      || current.phone.trim()
      || marketGeoHasValue(current.geo),
    );
    if (!hasContent) return;
    saveMarketDraft({
      userId,
      categoryId: current.categoryId,
      address: current.address,
      descr: current.descr,
      phone: current.phone,
      geo: current.geo,
      step: nextStep as MarketDraftStep,
    });
    setSavedAt(Date.now());
  }, [step, userId]);

  useEffect(() => {
    let cancelled = false;
    setCategoryFailure(null);
    (async () => {
      try {
        const list = await loadMarketCategories();
        if (cancelled) return;
        setCategories(list);
        if (initialCategoryId && list.some((c) => c.id === initialCategoryId && !c.isGroup)) {
          setCategoryId(initialCategoryId);
          setStep((current) => (current === 1 ? 2 : current));
        }
      } catch (e) {
        if (cancelled) return;
        logErrorToJournal(e, 'Витрина маркетплейса: категории мастера заявки');
        setCategoryFailure(toMarketFailure(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialCategoryId, categoryTick]);

  // Телефон из профиля — только как ПОДСКАЗКА для пустого поля (ввод
  // пользователя/черновик не перетираем). Ошибка чтения профиля не блокирует
  // заявку: поле остаётся пустым, причина уходит в журнал.
  useEffect(() => {
    if (!userId) return;
    void (async () => {
      try {
        const profile = await loadMarketClientProfile(userId);
        setProfilePhone(profile.phone || '');
        // Телефон из профиля — ПОДСКАЗКА для пустого поля: ввод пользователя
        // и черновик не перетираем.
        if (!phonePrefilledRef.current) {
          phonePrefilledRef.current = true;
          if (profile.phone) {
            setPhone((current) => (current.trim() ? current : profile.phone));
          }
        }
        // Статус гейта — по серверным меткам заявок (SSoT), без локальных
        // догадок. Ошибка чтения не блокирует мастер: статус останется
        // «неизвестен», а сервер проверит номер при создании заявки.
        try {
          setPhoneGate(await loadMyMarketPhoneGate(userId, profile.phone));
        } catch (e) {
          logErrorToJournal(e, 'Витрина маркетплейса: статус телефона для заявки');
        }
      } catch (e) {
        logErrorToJournal(e, 'Витрина маркетплейса: телефон из профиля');
      }
    })();
  }, [userId]);

  // Адресная книга профиля: до гидрации настроек показывать нечего — список
  // появится после ответа сервера (без выдуманных адресов).
  useEffect(() => {
    let cancelled = false;
    void settingsStore.hydrate().then(() => {
      if (!cancelled) setSavedAddresses(loadMarketAddresses());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Автосохранение черновика (короткая задержка — не пишем на каждый символ).
  useEffect(() => {
    if (!userId) return;
    const timer = setTimeout(() => persistDraft(step), 300);
    return () => clearTimeout(timer);
  }, [address, categoryId, descr, geo, phone, persistDraft, step, userId]);

  const selectable = (categories || []).filter((c) => !c.isGroup);
  const selectedCategory = selectable.find((c) => c.id === categoryId);

  const addPhotos = (files: File[]) => {
    setPhotoError('');
    if (files.length === 0) return;
    const next = [...photos];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setPhotoError(`Файл «${file.name}» не является изображением`);
        continue;
      }
      if (file.size > MARKET_MAX_PHOTO_BYTES) {
        setPhotoError(`Фото «${file.name}» больше 15 МБ`);
        continue;
      }
      if (next.length >= MARKET_MAX_PHOTOS) {
        setPhotoError(`Можно приложить не больше ${MARKET_MAX_PHOTOS} фото`);
        break;
      }
      next.push(file);
    }
    setPhotos(next);
  };

  const goBack = () => {
    setFieldError('');
    persistDraft(step);
    if (step > 1) {
      setStep((n) => n - 1);
      return;
    }
    onCancel();
  };

  const validateStep = (): string => {
    if (step === 1 && !categoryId) return 'Выберите категорию услуги';
    if (step === 2) {
      if (address.trim().length < 5) return 'Укажите адрес (не короче 5 символов)';
      const geoError = validateMarketGeo(geo);
      if (geoError) return geoError;
    }
    if (step === 3 && descr.trim().length < 10) return 'Опишите задачу хотя бы в двух словах';
    if (step === 4) {
      const digits = phone.replace(/\D/g, '');
      if (digits.length < 10) return 'Укажите телефон для связи (не менее 10 цифр)';
    }
    return '';
  };

  const submit = useCallback(async () => {
    setSubmitting(true);
    setFailure(null);
    try {
      if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
      const created = await createMarketRequest({ categoryId, address, descr, phone, geo });
      // Адрес — в «Мои адреса» (user_settings): повторно вводить не придётся.
      if (saveToAddressBook && address.trim().length >= 5) {
        try {
          rememberMarketAddressFromText(address);
        } catch (e) {
          logErrorToJournal(e, 'Витрина маркетплейса: сохранение адреса в профиль');
        }
      }
      let photoResult: PhotoUploadResult = { uploaded: 0, queued: 0, failures: [] };
      let skippedPhotos = 0;
      if (photos.length > 0) {
        if (created.queued) {
          skippedPhotos = photos.length;
        } else {
          photoResult = await uploadMarketRequestPhotos(created.id, photos);
        }
      }
      // Заявка отправлена — черновик больше не нужен.
      clearMarketDraft();
      clearMarketResume();
      setSavedAt(0);
      // Статус гейта — из созданной строки (сервер уже проставил метки).
      // Офлайн-очередь статуса не имеет: там заявка ещё не на сервере.
      let phoneState: MarketPhoneState | undefined;
      let phoneHint = '';
      if (!created.queued) {
        try {
          const fresh = await loadMarketRequest(created.id);
          phoneState = marketPhoneState(fresh);
          phoneHint = fresh.phoneVerificationHint;
        } catch (e) {
          logErrorToJournal(e, 'Витрина маркетплейса: статус телефона созданной заявки');
        }
      }
      setSuccess({
        requestId: created.id,
        queued: created.queued,
        photoResult,
        skippedPhotos,
        phoneState,
        phoneHint,
      });
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: создание заявки');
      setFailure(toMarketFailure(e));
    } finally {
      setSubmitting(false);
    }
  }, [address, categoryId, descr, geo, phone, photos, saveToAddressBook, userId]);

  const next = () => {
    const error = validateStep();
    if (error) {
      setFieldError(error);
      return;
    }
    setFieldError('');
    persistDraft(step);
    if (step < MARKET_WIZARD_STEPS.length) {
      setStep((n) => n + 1);
      return;
    }
    void submit();
  };

  // Гость мастер заявки не получает — только вход/регистрация.
  if (!userId) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title={t('market.new_request')} subtitle={t('market.login_required')} onBack={onCancel} />
        <div className="flex-1 overflow-y-auto">
          <AuthRequiredScreen
            title={t('market.leave_request')}
            description={t('market.signin_requests')}
            note={t('market.draft_saved_device')}
            onAction={onRequireAuth}
          />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title={t('market.request')} subtitle={t('market.done')} onBack={onHome} />
        <div className="flex-1 overflow-y-auto">
          <MarketWizardSuccess
            success={success}
            onOpenRequest={onOpenRequest}
            onOpenRequests={onOpenRequests}
            onHome={onHome}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title={t('market.new_request')}
        subtitle={t('market.step', { step, total: MARKET_WIZARD_STEPS.length, title: MARKET_WIZARD_STEPS[step - 1] })}
        onBack={goBack}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-5 p-4 pb-6 sm:p-6">
          <StepDots step={step} />

          {/* Состояние черновика — явное, без «тихого» сохранения */}
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Save className="h-3.5 w-3.5" />
            {savedAt > 0
              ? `Черновик сохранён на устройстве · ${new Date(savedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
              : 'Черновик сохраняется автоматически на этом устройстве'}
          </div>

          {fieldError && (
            <div className="flex items-start gap-2 rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{fieldError}</span>
            </div>
          )}

          {failure && <MarketError failure={failure} />}

          {step === 1 && (
            <MarketCategoryStep
              categories={categories}
              failure={categoryFailure}
              categoryId={categoryId}
              onSelect={(id) => {
                setCategoryId(id);
                setFieldError('');
              }}
              onRetry={() => setCategoryTick((n) => n + 1)}
            />
          )}
          {step === 2 && (
            <MarketAddressStep
              address={address}
              onChange={(value) => {
                setAddress(value);
                setFieldError('');
              }}
              geo={geo}
              onGeoChange={(value) => {
                setGeo(value);
                setFieldError('');
              }}
              savedAddresses={savedAddresses}
              onUseSavedAddress={(saved) => {
                setAddress(composeMarketAddress(saved));
                setFieldError('');
              }}
              saveAddress={saveToAddressBook}
              onToggleSaveAddress={setSaveToAddressBook}
            />
          )}
          {step === 3 && (
            <MarketDescriptionStep
              descr={descr}
              onChange={(value) => {
                setDescr(value);
                setFieldError('');
              }}
              photos={photos}
              photoError={photoError}
              onAddPhotos={addPhotos}
              onRemovePhoto={(index) => setPhotos((prev) => prev.filter((_, i) => i !== index))}
            />
          )}
          {step === 4 && (
            <MarketPhoneStep
              phone={phone}
              onChange={(value) => {
                setPhone(value);
                setFieldError('');
              }}
              categoryName={selectedCategory?.name || ''}
              address={address}
              descr={descr}
              photoCount={photos.length}
              offline={isEffectivelyOffline()}
              phoneGate={phoneGate}
              profilePhone={profilePhone}
            />
          )}

          {restoredDraft && photos.length === 0 && step === 3 && (
            <div className="rounded-2xl border border-dashed bg-card/50 px-3 py-2 text-[11px] leading-5 text-muted-foreground">
              Фотографии не входят в черновик — приложите их заново перед отправкой.
            </div>
          )}
        </div>
      </div>

      {/* Нижняя панель действий — крупные касания, всегда под рукой */}
      <div className="shrink-0 border-t bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex w-full max-w-2xl gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-12 flex-1 rounded-xl"
            onClick={goBack}
            disabled={submitting}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            {step > 1 ? 'Назад' : 'Отмена'}
          </Button>
          <Button
            type="button"
            className="h-12 flex-[1.6] rounded-xl"
            onClick={next}
            disabled={submitting || (step === 1 && (!!categoryFailure || categories === null))}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Отправляем…
              </>
            ) : step < MARKET_WIZARD_STEPS.length ? (
              <>
                Далее
                <ChevronRight className="ml-1 h-4 w-4" />
              </>
            ) : (
              <>
                <Send className="mr-1.5 h-4 w-4" />
                Отправить заявку
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
